import { draftChoiceTypes, requiredDraftChoiceIds } from './types.ts';
import type {
  DraftAnswers,
  DraftChoice,
  DraftChoiceOption,
  DraftOptionsRequest,
  DraftOptionsResponse,
  DraftScriptRequest,
  DraftScriptResponse,
} from './types.ts';

export const draftLimits = {
  ideaCharacters: 4000,
  answerCharacters: 2400,
  maxChoices: 8,
  maxOptions: 8,
  maxScriptCharacters: 30000,
  maxTitleCharacters: 160,
  maxLoglineCharacters: 400,
  maxLoglineWords: 30,
  maxRequestBytes: 120000,
} as const;

export class DraftValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DraftValidationError';
  }
}

export function validateOptionsRequest(value: unknown): DraftOptionsRequest {
  if (!isRecord(value)) {
    throw new DraftValidationError('Request body must be an object.');
  }

  return { idea: validateIdea(value.idea) };
}

export function validateGenerateRequest(value: unknown): DraftScriptRequest {
  if (!isRecord(value)) {
    throw new DraftValidationError('Request body must be an object.');
  }

  const idea = validateIdea(value.idea);
  const choices = validateChoices(value.choices);
  const answers = validateAnswers(value.answers, choices);

  return { idea, choices, answers };
}

export function validateOptionsResponse(value: unknown): DraftOptionsResponse {
  if (!isRecord(value)) {
    throw new DraftValidationError('The model returned an invalid choice set.');
  }

  return { choices: validateChoices(value.choices) };
}

export function validateScriptResponse(value: unknown): DraftScriptResponse {
  if (!isRecord(value)) {
    throw new DraftValidationError('The model returned an invalid script.');
  }

  const title = validateText(value.title, 'script title', draftLimits.maxTitleCharacters);
  const logline = validateLogline(value.logline);
  const script = validateText(value.script, 'script', draftLimits.maxScriptCharacters);

  return { title, logline, script };
}

export function validateChoices(value: unknown): DraftChoice[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > draftLimits.maxChoices) {
    throw new DraftValidationError(`The choice set must contain 1-${draftLimits.maxChoices} choices.`);
  }

  const ids = new Set<string>();
  const choices = value.map((choice, index) => {
    const validated = validateChoice(choice, index);
    if (ids.has(validated.id)) {
      throw new DraftValidationError(`Choice ${index + 1} has a duplicate id.`);
    }
    ids.add(validated.id);
    return validated;
  });

  for (const requiredId of requiredDraftChoiceIds) {
    const choice = choices.find((item) => item.id === requiredId);
    if (!choice) {
      throw new DraftValidationError(`The choice set must include '${requiredId}'.`);
    }
    if (!choice.required) {
      throw new DraftValidationError(`Choice '${requiredId}' must be required.`);
    }
    if (choice.type !== 'select') {
      throw new DraftValidationError(`Choice '${requiredId}' must be a select control.`);
    }
  }

  return choices;
}

function validateChoice(value: unknown, index: number): DraftChoice {
  if (!isRecord(value)) {
    throw new DraftValidationError(`Choice ${index + 1} must be an object.`);
  }

  const id = validateText(value.id, `choice ${index + 1} id`, 64);
  if (!/^[a-z][a-z0-9_-]*$/.test(id)) {
    throw new DraftValidationError(`Choice ${index + 1} has an invalid id.`);
  }

  const type = value.type;
  if (typeof type !== 'string' || !draftChoiceTypes.includes(type as (typeof draftChoiceTypes)[number])) {
    throw new DraftValidationError(`Choice ${index + 1} has an unsupported input type.`);
  }

  const label = validateText(value.label, `choice ${index + 1} label`, 180);
  const helpText = validateNullableText(value.helpText, `choice ${index + 1} help text`, 400);
  const placeholder = validateNullableText(value.placeholder, `choice ${index + 1} placeholder`, 180);
  const required = value.required;
  if (typeof required !== 'boolean') {
    throw new DraftValidationError(`Choice ${index + 1} required must be boolean.`);
  }

  const options = validateChoiceOptions(value.options, type as DraftChoice['type'], index);
  return {
    id,
    type: type as DraftChoice['type'],
    label,
    helpText,
    required,
    options,
    placeholder,
  };
}

function validateChoiceOptions(value: unknown, type: DraftChoice['type'], index: number): DraftChoiceOption[] {
  if (!Array.isArray(value) || value.length > draftLimits.maxOptions) {
    throw new DraftValidationError(`Choice ${index + 1} has too many options.`);
  }

  const requiresOptions = type === 'select' || type === 'multiselect';
  if (requiresOptions && (value.length < 2 || value.length > draftLimits.maxOptions)) {
    throw new DraftValidationError(`Choice ${index + 1} must have 2-${draftLimits.maxOptions} options.`);
  }
  if (!requiresOptions && value.length !== 0) {
    throw new DraftValidationError(`Text choice ${index + 1} cannot have options.`);
  }

  const values = new Set<string>();
  return value.map((option, optionIndex) => {
    if (!isRecord(option)) {
      throw new DraftValidationError(`Choice ${index + 1} option ${optionIndex + 1} must be an object.`);
    }
    const validated = {
      value: validateText(option.value, `choice ${index + 1} option value`, 120),
      label: validateText(option.label, `choice ${index + 1} option label`, 180),
    };
    if (values.has(validated.value)) {
      throw new DraftValidationError(`Choice ${index + 1} has duplicate option values.`);
    }
    values.add(validated.value);
    return validated;
  });
}

function validateAnswers(value: unknown, choices: DraftChoice[]): DraftAnswers {
  if (!isRecord(value)) {
    throw new DraftValidationError('Answers must be an object.');
  }

  const choiceById = new Map(choices.map((choice) => [choice.id, choice]));
  for (const key of Object.keys(value)) {
    if (!choiceById.has(key)) {
      throw new DraftValidationError(`Answer '${key}' does not match a generated choice.`);
    }
  }

  const answers: DraftAnswers = {};
  for (const choice of choices) {
    const answer = value[choice.id];
    const normalized = validateAnswer(answer, choice);
    if (normalized !== undefined) {
      answers[choice.id] = normalized;
    }
    if (choice.required && isEmptyAnswer(normalized)) {
      throw new DraftValidationError(`Please answer '${choice.label}'.`);
    }
  }

  return answers;
}

function validateAnswer(value: unknown, choice: DraftChoice): string | string[] | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (choice.type === 'multiselect') {
    if (!Array.isArray(value)) {
      throw new DraftValidationError(`Answer '${choice.label}' must be a list.`);
    }
    const allowed = new Set(choice.options.map((option) => option.value));
    const values = value.map((item) => validateText(item, `answer '${choice.label}'`, draftLimits.answerCharacters));
    if (values.some((item) => !allowed.has(item))) {
      throw new DraftValidationError(`Answer '${choice.label}' contains an invalid option.`);
    }
    return Array.from(new Set(values));
  }

  const answer = validateText(value, `answer '${choice.label}'`, draftLimits.answerCharacters);
  if (choice.type === 'select' && !choice.options.some((option) => option.value === answer)) {
    throw new DraftValidationError(`Answer '${choice.label}' contains an invalid option.`);
  }
  return answer;
}

function validateIdea(value: unknown) {
  return validateText(value, 'idea', draftLimits.ideaCharacters);
}

function validateLogline(value: unknown) {
  const logline = validateText(value, 'script logline', draftLimits.maxLoglineCharacters);
  if (logline.split(/\s+/).length > draftLimits.maxLoglineWords) {
    throw new DraftValidationError(`script logline must be ${draftLimits.maxLoglineWords} words or fewer.`);
  }
  return logline;
}

function validateText(value: unknown, label: string, maxCharacters: number): string {
  if (typeof value !== 'string') {
    throw new DraftValidationError(`${label} must be text.`);
  }
  const text = value.trim();
  if (!text) {
    throw new DraftValidationError(`${label} cannot be empty.`);
  }
  if (text.length > maxCharacters) {
    throw new DraftValidationError(`${label} is too long.`);
  }
  return text;
}

function validateNullableText(value: unknown, label: string, maxCharacters: number): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return validateText(value, label, maxCharacters);
}

function isEmptyAnswer(value: string | string[] | undefined) {
  return value === undefined || (Array.isArray(value) ? value.length === 0 : value.length === 0);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
